import dicomParser from 'dicom-parser';
const TARGET_SIZE = 256;
function resolveUrl(src) {
    return src.startsWith('/') ? window.location.origin + src : src;
}
function isDicomData(buf) {
    if (buf.byteLength < 132)
        return false;
    const view = new Uint8Array(buf, 128, 4);
    return view[0] === 0x44 && view[1] === 0x49 && view[2] === 0x43 && view[3] === 0x4d; // "DICM"
}
function renderDicomToCanvas(arrayBuffer) {
    const byteArray = new Uint8Array(arrayBuffer);
    const dataSet = dicomParser.parseDicom(byteArray);
    const width = dataSet.uint16('x00280011') || 512;
    const height = dataSet.uint16('x00280010') || 512;
    const bitsAllocated = dataSet.uint16('x00280100') || 16;
    const bitsStored = dataSet.uint16('x00280101') || 12;
    const pixelRepresentation = dataSet.uint16('x00280103') || 0;
    const samplesPerPixel = dataSet.uint16('x00280002') || 1;
    const photometricInterpretation = dataSet.string('x00280004') || 'MONOCHROME2';
    let windowCenter = dataSet.floatString('x00281050');
    let windowWidth = dataSet.floatString('x00281051');
    const rescaleSlope = dataSet.floatString('x00281053') || 1;
    const rescaleIntercept = dataSet.floatString('x00281052') || 0;
    const pixelDataElement = dataSet.elements.x7fe00010;
    if (!pixelDataElement)
        throw new Error('No pixel data');
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    const imageData = ctx.createImageData(width, height);
    const pixelDataOffset = pixelDataElement.dataOffset;
    const pixelDataLength = pixelDataElement.length;
    const storedRange = 2 ** bitsStored;
    const storedMin = pixelRepresentation === 1 ? -(storedRange / 2) : 0;
    const storedMax = pixelRepresentation === 1 ? storedRange / 2 - 1 : storedRange - 1;
    const storedMask = storedRange - 1;
    const normalize = (raw) => {
        const masked = raw & storedMask;
        if (pixelRepresentation === 1) {
            const signBit = 1 << (bitsStored - 1);
            return masked & signBit ? masked - storedRange : masked;
        }
        return masked;
    };
    let minPixel = Infinity;
    let maxPixel = -Infinity;
    const pixelValues = [];
    if (bitsAllocated === 16) {
        const pxData = new DataView(arrayBuffer, pixelDataOffset, pixelDataLength);
        const numPixels = width * height * samplesPerPixel;
        for (let i = 0; i < numPixels && i * 2 < pixelDataLength; i++) {
            let v = pixelRepresentation === 1 ? pxData.getInt16(i * 2, true) : pxData.getUint16(i * 2, true);
            v = normalize(v);
            v = Math.min(Math.max(v, storedMin), storedMax);
            v = v * rescaleSlope + rescaleIntercept;
            pixelValues.push(v);
            if (v < minPixel)
                minPixel = v;
            if (v > maxPixel)
                maxPixel = v;
        }
    }
    else if (bitsAllocated === 8) {
        const pxData = new DataView(arrayBuffer, pixelDataOffset, pixelDataLength);
        for (let i = 0; i < pixelDataLength; i++) {
            const raw = pixelRepresentation === 1 ? pxData.getInt8(i) : pxData.getUint8(i);
            let v = normalize(raw);
            v = Math.min(Math.max(v, storedMin), storedMax);
            v = v * rescaleSlope + rescaleIntercept;
            pixelValues.push(v);
            if (v < minPixel)
                minPixel = v;
            if (v > maxPixel)
                maxPixel = v;
        }
    }
    if (!windowWidth || !windowCenter) {
        windowWidth = maxPixel - minPixel;
        windowCenter = (maxPixel + minPixel) / 2;
    }
    const wMin = windowCenter - windowWidth / 2;
    const wMax = windowCenter + windowWidth / 2;
    const isInverted = photometricInterpretation === 'MONOCHROME1';
    for (let i = 0; i < pixelValues.length; i++) {
        let v = pixelValues[i];
        if (v <= wMin)
            v = 0;
        else if (v >= wMax)
            v = 255;
        else
            v = ((v - wMin) / windowWidth) * 255;
        if (isInverted)
            v = 255 - v;
        const idx = i * 4;
        imageData.data[idx] = v;
        imageData.data[idx + 1] = v;
        imageData.data[idx + 2] = v;
        imageData.data[idx + 3] = 255;
    }
    ctx.putImageData(imageData, 0, 0);
    return canvas;
}
function scaleToTarget(source) {
    const sw = source instanceof HTMLCanvasElement ? source.width : source.naturalWidth;
    const sh = source instanceof HTMLCanvasElement ? source.height : source.naturalHeight;
    const scale = Math.min(TARGET_SIZE / sw, TARGET_SIZE / sh, 1);
    const tw = Math.round(sw * scale);
    const th = Math.round(sh * scale);
    const out = document.createElement('canvas');
    out.width = tw;
    out.height = th;
    const ctx = out.getContext('2d');
    ctx.drawImage(source, 0, 0, tw, th);
    return out.toDataURL('image/jpeg', 0.85).replace(/^data:image\/jpeg;base64,/, '');
}
function loadImage(url) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
        img.src = url;
    });
}
async function thumbnailToBase64(storageLocation, label) {
    const url = resolveUrl(storageLocation);
    const response = await fetch(url);
    if (!response.ok)
        throw new Error(`Fetch failed: ${response.status}`);
    const arrayBuffer = await response.arrayBuffer();
    if (isDicomData(arrayBuffer)) {
        const canvas = renderDicomToCanvas(arrayBuffer);
        return { base64: scaleToTarget(canvas), mediaType: 'image/jpeg', label };
    }
    const blob = new Blob([arrayBuffer]);
    const blobUrl = URL.createObjectURL(blob);
    try {
        const img = await loadImage(blobUrl);
        return { base64: scaleToTarget(img), mediaType: 'image/jpeg', label };
    }
    finally {
        URL.revokeObjectURL(blobUrl);
    }
}
const MAX_THUMBNAILS = 8;
export async function collectSeriesThumbnails(seriesList, selectedUids) {
    // The thumbnail's cloud URL when its upload has landed, otherwise the LAN
    // capability URL for the clinic's own node, which for an onsite object is the
    // only address it has (see lib/dicom/imageAvailability). A series with neither
    // contributes no image rather than a fetch that cannot succeed.
    const selected = [];
    for (const s of seriesList) {
        if (!selectedUids.has(s.seriesUid))
            continue;
        const thumb = s.thumbnails[0];
        const url = thumb?.storageLocation ?? thumb?.localStorageLocation;
        if (!url)
            continue;
        selected.push({
            url,
            label: s.seriesDescription || s.modality || `Series ${selected.length + 1}`,
        });
        if (selected.length === MAX_THUMBNAILS)
            break;
    }
    const results = await Promise.allSettled(selected.map(({ url, label }) => thumbnailToBase64(url, label)));
    return results
        .filter((r) => r.status === 'fulfilled')
        .map((r) => r.value);
}
